import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { OrangeSection } from '../components/ui/OrangeSection'
import { 
  Smartphone, 
  Camera, 
  Search, 
  Package, 
  Plus,
  BarChart3,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  QrCode,
  Upload,
  Download,
  Settings,
  Menu,
  Bell,
  User,
  MapPin,
  DollarSign,
  Calendar,
  Filter,
  RefreshCw
} from 'lucide-react'

interface QuickStats {
  totalItems: number
  itemsToday: number
  totalValue: number
  pendingTasks: number
}

interface RecentItem {
  id: string
  name: string
  sku: string
  status: string
  location: string
  value: number
  timestamp: string
}

interface QuickAction {
  id: string
  name: string
  icon: React.ComponentType<any>
  href: string
  color: string
  description: string
}

const mockStats: QuickStats = {
  totalItems: 1247,
  itemsToday: 23,
  totalValue: 125430.50,
  pendingTasks: 5
}

const mockRecentItems: RecentItem[] = [
  {
    id: '1',
    name: 'Vintage Camera',
    sku: 'VC-2023-001',
    status: 'Available',
    location: 'Warehouse A',
    value: 250.00,
    timestamp: '2 hours ago'
  },
  {
    id: '2',
    name: 'Designer Watch',
    sku: 'DW-2023-045',
    status: 'Reserved',
    location: 'Store Front',
    value: 450.00,
    timestamp: '4 hours ago'
  },
  {
    id: '3',
    name: 'Antique Book',
    sku: 'AB-2023-012',
    status: 'Sold',
    location: 'Warehouse B',
    value: 75.00,
    timestamp: '6 hours ago'
  }
]

const quickActions: QuickAction[] = [
  {
    id: 'scan',
    name: 'Scan Item',
    icon: Camera,
    href: '/qr-scanner',
    color: 'bg-blue-500',
    description: 'Scan QR codes and barcodes'
  },
  {
    id: 'add',
    name: 'Add Item',
    icon: Plus,
    href: '/intake',
    color: 'bg-green-500',
    description: 'Add new inventory item'
  },
  {
    id: 'search',
    name: 'Search',
    icon: Search,
    href: '/search',
    color: 'bg-purple-500',
    description: 'Find items quickly'
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: BarChart3,
    href: '/analytics',
    color: 'bg-orange-500',
    description: 'View reports and stats'
  }
]

export default function MobilePage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications] = useState([
    { id: '1', message: 'Low stock alert: 3 items below threshold', type: 'warning', time: '5 min ago' },
    { id: '2', message: 'New item added: Vintage Camera', type: 'info', time: '1 hour ago' },
    { id: '3', message: 'Weekly report ready', type: 'success', time: '2 hours ago' }
  ])

  const getStatusBadge = (status: string) => {
    const variants = {
      'Available': 'success',
      'Reserved': 'warning',
      'Sold': 'info',
      'Damaged': 'error'
    } as const

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    )
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        fontFamily: 'Rajdhani, sans-serif'
      }}
    >
      {/* Mobile Header */}
      <div
        className="sticky top-0 z-50 border-b"
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(37,40,57,0.8) 100%)',
          backdropFilter: 'blur(12px)',
          borderColor: 'rgba(251,146,60,0.3)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        }}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                boxShadow: '0 4px 12px rgba(251,146,60,0.4)'
              }}
            >
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1
                className="text-lg font-bold"
                style={{
                  color: '#fb923c',
                  textShadow: '0 0 10px rgba(251,146,60,0.5)'
                }}
              >
                BGS Core
              </h1>
              <p className="text-xs" style={{ color: '#9ca3af' }}>Mobile Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowNotifications(!showNotifications)}
              size="sm"
              variant="secondary"
              className="relative"
              style={{
                background: 'rgba(251,146,60,0.1)',
                borderColor: 'rgba(251,146,60,0.3)',
                color: '#fb923c'
              }}
            >
              <Bell className="h-4 w-4" />
              {notifications.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full text-xs flex items-center justify-center text-white"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    boxShadow: '0 0 8px rgba(239,68,68,0.6)'
                  }}
                >
                  {notifications.length}
                </span>
              )}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              style={{
                background: 'rgba(251,146,60,0.1)',
                borderColor: 'rgba(251,146,60,0.3)',
                color: '#fb923c'
              }}
            >
              <User className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div
          className="absolute top-16 right-4 z-40 w-80 rounded-lg"
          style={{
            background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(251,146,60,0.3)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(251,146,60,0.2)'
          }}
        >
          <div
            className="p-4 border-b"
            style={{ borderColor: 'rgba(251,146,60,0.2)' }}
          >
            <h3 className="font-medium" style={{ color: '#fb923c' }}>Notifications</h3>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="p-3 border-b last:border-b-0"
                style={{ borderColor: 'rgba(251,146,60,0.1)' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-2 h-2 rounded-full mt-2"
                    style={{
                      backgroundColor: notification.type === 'warning' ? '#eab308' :
                        notification.type === 'success' ? '#22c55e' : '#3b82f6',
                      boxShadow: `0 0 8px ${
                        notification.type === 'warning' ? 'rgba(234,179,8,0.6)' :
                        notification.type === 'success' ? 'rgba(34,197,94,0.6)' : 'rgba(59,130,246,0.6)'
                      }`
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm" style={{ color: '#e5e7eb' }}>{notification.message}</p>
                    <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>{notification.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Quick Stats */}
        <OrangeSection>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-white" />
              <h2
                className="text-lg font-medium"
                style={{
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(to right, #fb923c, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text'
                }}
              >
                Quick Stats
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="text-center p-3 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                  border: '1px solid rgba(251,146,60,0.2)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <p
                  className="text-2xl font-bold"
                  style={{ color: '#fb923c', textShadow: '0 0 10px rgba(251,146,60,0.3)' }}
                >
                  {mockStats.totalItems.toLocaleString()}
                </p>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Total Items</p>
              </div>
              <div
                className="text-center p-3 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                  border: '1px solid rgba(251,146,60,0.2)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <p
                  className="text-2xl font-bold"
                  style={{ color: '#fb923c', textShadow: '0 0 10px rgba(251,146,60,0.3)' }}
                >
                  {mockStats.itemsToday}
                </p>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Today</p>
              </div>
              <div
                className="text-center p-3 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                  border: '1px solid rgba(251,146,60,0.2)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <p
                  className="text-2xl font-bold"
                  style={{ color: '#fb923c', textShadow: '0 0 10px rgba(251,146,60,0.3)' }}
                >
                  ${mockStats.totalValue.toLocaleString()}
                </p>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Total Value</p>
              </div>
              <div
                className="text-center p-3 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                  border: '1px solid rgba(251,146,60,0.2)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <p
                  className="text-2xl font-bold"
                  style={{ color: '#fb923c', textShadow: '0 0 10px rgba(251,146,60,0.3)' }}
                >
                  {mockStats.pendingTasks}
                </p>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Pending</p>
              </div>
            </div>
          </div>
        </OrangeSection>

        {/* Quick Actions */}
        <OrangeSection>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-white" />
              <h2
                className="text-lg font-medium"
                style={{
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(to right, #fb923c, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text'
                }}
              >
                Quick Actions
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon
                const actionColors = {
                  'bg-blue-500': { from: '#3b82f6', to: '#2563eb' },
                  'bg-green-500': { from: '#22c55e', to: '#16a34a' },
                  'bg-purple-500': { from: '#a855f7', to: '#9333ea' },
                  'bg-orange-500': { from: '#fb923c', to: '#f97316' }
                }
                const colors = actionColors[action.color as keyof typeof actionColors] || { from: '#fb923c', to: '#f97316' }

                return (
                  <Link key={action.id} to={action.href}>
                    <div
                      className="p-4 rounded-lg text-white text-center transition-all duration-300"
                      style={{
                        background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)`,
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: `0 4px 12px rgba(0,0,0,0.3), 0 0 20px ${colors.from}40`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)'
                        e.currentTarget.style.boxShadow = `0 8px 20px rgba(0,0,0,0.4), 0 0 30px ${colors.from}60`
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)'
                        e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.3), 0 0 20px ${colors.from}40`
                      }}
                    >
                      <Icon className="h-6 w-6 mx-auto mb-2" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
                      <p className="font-medium text-sm">{action.name}</p>
                      <p className="text-xs mt-1" style={{ opacity: 0.9 }}>{action.description}</p>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </OrangeSection>

        {/* Search Bar */}
        <OrangeSection>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-white" />
              <h2
                className="text-lg font-medium"
                style={{
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(to right, #fb923c, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text'
                }}
              >
                Quick Search
              </h2>
            </div>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4"
                style={{ color: '#9ca3af' }}
              />
              <input
                type="text"
                placeholder="Search items, SKU, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg text-white focus:outline-none transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                  border: '1px solid rgba(251,146,60,0.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(251,146,60,0.5)'
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4), 0 0 20px rgba(251,146,60,0.3)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(251,146,60,0.3)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'
                }}
              />
            </div>
          </div>
        </OrangeSection>

        {/* Recent Items */}
        <OrangeSection>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-white" />
                <h2
                  className="text-lg font-medium"
                  style={{
                    color: 'transparent',
                    backgroundImage: 'linear-gradient(to right, #fb923c, #ec4899)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text'
                  }}
                >
                  Recent Items
                </h2>
              </div>
              <Link to="/search">
                <Button
                  size="sm"
                  variant="secondary"
                  style={{
                    background: 'rgba(251,146,60,0.1)',
                    borderColor: 'rgba(251,146,60,0.3)',
                    color: '#fb923c'
                  }}
                >
                  View All
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {mockRecentItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, rgba(31,41,55,0.6) 0%, rgba(17,24,39,0.6) 100%)',
                    border: '1px solid rgba(251,146,60,0.2)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(251,146,60,0.4)'
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4), 0 0 15px rgba(251,146,60,0.2)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(251,146,60,0.2)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium text-sm">{item.name}</p>
                      {getStatusBadge(item.status)}
                    </div>
                    <p className="text-xs font-mono" style={{ color: '#fb923c' }}>{item.sku}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs" style={{ color: '#9ca3af' }}>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {item.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        ${item.value}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {item.timestamp}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    style={{
                      background: 'rgba(251,146,60,0.1)',
                      borderColor: 'rgba(251,146,60,0.3)',
                      color: '#fb923c'
                    }}
                  >
                    <QrCode className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </OrangeSection>

        {/* Mobile Navigation */}
        <div
          className="fixed bottom-0 left-0 right-0 border-t"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(37,40,57,0.9) 100%)',
            backdropFilter: 'blur(12px)',
            borderColor: 'rgba(251,146,60,0.3)',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.4)'
          }}
        >
          <div className="grid grid-cols-4 py-2">
            <Link
              to="/"
              className="flex flex-col items-center py-2 text-center transition-all duration-300"
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#fb923c'
                if (text) (text as HTMLElement).style.color = '#fb923c'
              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#9ca3af'
                if (text) (text as HTMLElement).style.color = '#9ca3af'
              }}
            >
              <BarChart3 className="h-5 w-5 mb-1" style={{ color: '#9ca3af' }} />
              <span className="text-xs" style={{ color: '#9ca3af' }}>Home</span>
            </Link>
            <Link
              to="/intake"
              className="flex flex-col items-center py-2 text-center transition-all duration-300"
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#fb923c'
                if (text) (text as HTMLElement).style.color = '#fb923c'
              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#9ca3af'
                if (text) (text as HTMLElement).style.color = '#9ca3af'
              }}
            >
              <Plus className="h-5 w-5 mb-1" style={{ color: '#9ca3af' }} />
              <span className="text-xs" style={{ color: '#9ca3af' }}>Add</span>
            </Link>
            <Link
              to="/qr-scanner"
              className="flex flex-col items-center py-2 text-center transition-all duration-300"
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#fb923c'
                if (text) (text as HTMLElement).style.color = '#fb923c'
              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#9ca3af'
                if (text) (text as HTMLElement).style.color = '#9ca3af'
              }}
            >
              <Camera className="h-5 w-5 mb-1" style={{ color: '#9ca3af' }} />
              <span className="text-xs" style={{ color: '#9ca3af' }}>Scan</span>
            </Link>
            <Link
              to="/search"
              className="flex flex-col items-center py-2 text-center transition-all duration-300"
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#fb923c'
                if (text) (text as HTMLElement).style.color = '#fb923c'
              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('svg')
                const text = e.currentTarget.querySelector('span')
                if (icon) (icon as HTMLElement).style.color = '#9ca3af'
                if (text) (text as HTMLElement).style.color = '#9ca3af'
              }}
            >
              <Search className="h-5 w-5 mb-1" style={{ color: '#9ca3af' }} />
              <span className="text-xs" style={{ color: '#9ca3af' }}>Search</span>
            </Link>
          </div>
        </div>

        {/* Bottom Spacing for Fixed Navigation */}
        <div className="h-20" />
      </div>
    </div>
  )
}
